import { useEffect, useState } from "react";
import { api, money, catColor } from "@/hub/lib/api";
import { Package as PackageIcon, Plus, Pencil, Trash2, Check, Lock, Soup } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";

function useRole() {
  const [role, setRole] = useState(() => localStorage.getItem("lush_role") || "RM");
  useEffect(() => {
    const h = () => setRole(localStorage.getItem("lush_role") || "RM");
    window.addEventListener("role-change", h);
    return () => window.removeEventListener("role-change", h);
  }, []);
  return role;
}

const TYPE_BADGE = { Training: "bg-terracotta text-white", Custom: "bg-blue-600 text-white", Standard: "bg-forest text-white" };

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activities, setActivities] = useState([]);
  const [menus, setMenus] = useState([]);
  const role = useRole();
  const isAdmin = role === "Admin";
  const load = () => api.packages().then(setPackages);
  useEffect(() => {
    load();
    api.categories().then(setCategories);
    api.activities().then(setActivities);
    api.menus().then(setMenus);
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-500 max-w-lg">Base packages bundle selected category items and standard menus into one overall rate. Anything not included applies as a top-up per group. {!isAdmin && <span className="inline-flex items-center gap-1 text-terracotta"><Lock className="h-3 w-3" /> Switch to Admin to edit.</span>}</p>
        {isAdmin && <PackageSheet categories={categories} activities={activities} menus={menus} onDone={load} />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {packages.map((p) => (
          <div key={p.id} className="bg-white border border-border flex flex-col" data-testid={`package-card-${p.id}`}>
            <div className="p-5 border-b border-border">
              <div className="flex items-start justify-between">
                <div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold uppercase tracking-wider ${TYPE_BADGE[p.type] || TYPE_BADGE.Standard}`}>{p.type}</span>
                  <h3 className="font-heading text-xl font-medium mt-2">{p.name}</h3>
                  <p className="text-xs text-stone-400">{p.duration}</p>
                </div>
                <PackageIcon className="h-5 w-5 text-stone-300" />
              </div>
              <div className="mt-3 font-mono text-3xl font-light tracking-tighter text-forest">{money(p.base_price)}</div>
              <div className="text-[10px] uppercase tracking-wider text-stone-400">Overall rate</div>
            </div>
            <div className="p-5 flex-1">
              <ul className="space-y-1.5">
                {p.inclusions.map((inc, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                    <Check className="h-4 w-4 text-forest shrink-0 mt-0.5" strokeWidth={2} /> {inc}
                  </li>
                ))}
                {p.inclusions.length === 0 && <li className="text-xs text-stone-400">No items selected.</li>}
              </ul>
              {(p.menus || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-dashed border-stone-200 space-y-2">
                  {p.menus.map((mn, i) => {
                    const full = menus.find((m) => m.id === mn.menu_id);
                    return (
                      <div key={i}>
                        <div className="inline-flex items-center gap-1 text-[11px] font-semibold bg-forest/10 text-forest px-1.5 py-0.5 rounded-sm"><Soup className="h-3 w-3" /> {mn.name}</div>
                        {full && (
                          <ul className="mt-1 ml-1.5 space-y-0.5" data-testid={`pkg-menu-plan-${p.id}`}>
                            {full.components.map((c, j) => (
                              <li key={j} className="text-[11px] text-stone-500">Day {c.day} · {c.meal}{c.detail ? ` — ${c.detail}` : ""}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {isAdmin && (
              <div className="p-3 border-t border-border flex gap-2">
                <PackageSheet existing={p} categories={categories} activities={activities} menus={menus} onDone={load} trigger={
                  <Button variant="outline" size="sm" className="rounded-sm flex-1" data-testid={`edit-package-${p.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                } />
                <Button variant="outline" size="icon" className="rounded-sm h-9 w-9 text-red-500 hover:bg-red-50" data-testid={`delete-package-${p.id}`}
                  onClick={() => { if (window.confirm("Delete package?")) api.deletePackage(p.id).then(() => { toast.success("Deleted"); load(); }); }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PackageSheet({ existing, categories, activities, menus, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "Standard", base_price: "", duration: "1 Day", description: "" });
  const [selected, setSelected] = useState([]);
  const [selectedMenus, setSelectedMenus] = useState([]);

  useEffect(() => {
    if (open) {
      setForm(existing
        ? { name: existing.name, type: existing.type, base_price: existing.base_price, duration: existing.duration, description: existing.description || "" }
        : { name: "", type: "Standard", base_price: "", duration: "1 Day", description: "" });
      setSelected(existing?.items?.map((i) => i.activity_id).filter(Boolean) || []);
      setSelectedMenus(existing?.menus?.map((m) => m.menu_id).filter(Boolean) || []);
    }
  }, [open, existing]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleMenu = (id) => setSelectedMenus((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const grouped = categories.map((c) => ({ cat: c.name, items: activities.filter((a) => a.category === c.name) })).filter((g) => g.items.length);
  const selectedItems = activities.filter((a) => selected.includes(a.id));
  const chosenMenus = menus.filter((m) => selectedMenus.includes(m.id));
  const itemsSum = selectedItems.reduce((s, a) => s + a.cost, 0);

  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    const items = selectedItems.map((a) => ({ activity_id: a.id, name: a.name, category: a.category, price: a.cost }));
    const menuRefs = chosenMenus.map((m) => ({ menu_id: m.id, name: m.name, price: m.price }));
    const inclusions = [...items.map((i) => i.name), ...menuRefs.map((m) => `Menu: ${m.name}`)];
    const payload = { ...form, base_price: Number(form.base_price) || 0, items, menus: menuRefs, inclusions };
    try {
      if (existing) await api.updatePackage(existing.id, payload);
      else await api.createPackage(payload);
      toast.success(existing ? "Package updated" : "Package created");
      setOpen(false); onDone();
    } catch { toast.error("Save failed"); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        {trigger || <Button size="sm" className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="new-package-btn"><Plus className="h-4 w-4 mr-1" /> New Package</Button>}
      </SheetTrigger>
      <SheetContent className="overflow-y-auto lush-scroll w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="font-heading">{existing ? "Edit Package" : "New Package"}</SheetTitle>
          <SheetDescription>Bundle category items and standard menus into one overall rate.</SheetDescription>
        </SheetHeader>
        <div className="space-y-3 mt-5">
          <div><Label className="text-xs uppercase tracking-wider text-stone-400">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="pf-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-stone-400">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="rounded-sm" data-testid="pf-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Standard">Standard</SelectItem><SelectItem value="Training">Training</SelectItem><SelectItem value="Custom">Custom</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-stone-400">Duration</Label><Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="rounded-sm" data-testid="pf-duration" /></div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-400">Overall Rate (LKR)</Label>
            <Input type="number" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} className="rounded-sm font-mono" data-testid="pf-price" />
            {itemsSum > 0 && <p className="text-[11px] text-stone-400 mt-1">Individual prices of selected items total {money(itemsSum)} — your overall rate can differ.</p>}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-400 flex items-center gap-1.5"><Soup className="h-3.5 w-3.5" /> Standard Menus</Label>
            <div className="border border-border rounded-sm divide-y divide-border max-h-40 overflow-auto lush-scroll mt-1">
              {menus.length === 0 && <div className="px-3 py-3 text-xs text-stone-400">No menus yet. Create them in Admin Settings → Menus.</div>}
              {menus.map((m) => (
                <label key={m.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-stone-50" data-testid={`pf-menu-${m.id}`}>
                  <Checkbox checked={selectedMenus.includes(m.id)} onCheckedChange={() => toggleMenu(m.id)} className="rounded-sm" />
                  <span className="flex-1">{m.name} <span className="text-xs text-stone-400">· {m.days}d · {m.components.length} items</span></span>
                  <span className="font-mono text-xs text-stone-400">{money(m.price)}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-400">Included Items (from categories)</Label>
            <div className="border border-border rounded-sm divide-y divide-border max-h-64 overflow-auto lush-scroll mt-1">
              {grouped.length === 0 && <div className="px-3 py-4 text-xs text-stone-400">No categories/items yet. Add them in Admin Settings & Activities.</div>}
              {grouped.map(({ cat, items }) => (
                <div key={cat}>
                  <div className="px-3 py-1.5 bg-stone-50 flex items-center gap-2 sticky top-0">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: catColor(cat) }} />
                    <span className="text-xs font-semibold text-stone-600">{cat}</span>
                  </div>
                  {items.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-stone-50" data-testid={`pf-item-${a.id}`}>
                      <Checkbox checked={selected.includes(a.id)} onCheckedChange={() => toggle(a.id)} className="rounded-sm" />
                      <span className="flex-1">{a.name}</span>
                      <span className="font-mono text-xs text-stone-400">{money(a.cost)}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-stone-400 mt-1">{selected.length} items · {selectedMenus.length} menus included · everything else becomes a per-group top-up.</p>
          </div>

          <div><Label className="text-xs uppercase tracking-wider text-stone-400">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" rows={2} data-testid="pf-desc" /></div>
        </div>
        <SheetFooter className="mt-5"><Button onClick={submit} className="rounded-sm bg-forest hover:bg-forest-hover w-full" data-testid="save-package-btn">{existing ? "Save Changes" : "Create Package"}</Button></SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
